import { useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import Button from '@/elements/Button'
import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import useBreakpoints from '@/hooks/useBreakpoints'
import type { DropdownPosition } from '@/hooks/useDropdown'

import './DropdownMenu.scss'

interface DropdownMenuProps {
  open: boolean
  onClose?: () => void
  position: DropdownPosition
  closeWithoutSaving?: () => void
  menuRef?: React.RefObject<HTMLDivElement>
  children: ReactNode
  title?: string
  className?: string
  optionsAutoWidth?: boolean
  noVerticalScroll?: boolean
  isMulti?: boolean
  isSearchable?: boolean
}

const DropdownMenu = ({
  open,
  onClose,
  position,
  closeWithoutSaving,
  menuRef: externalMenuRef,
  children,
  title = 'Select an option',
  className,
  optionsAutoWidth = false,
  noVerticalScroll = false,
  isMulti = false,
  isSearchable = false,
}: DropdownMenuProps) => {
  const { isMobile } = useBreakpoints()
  const internalMenuRef = useRef<HTMLDivElement>(null)
  const menuRef = externalMenuRef || internalMenuRef

  if (!open) return null

  return createPortal(
    <div
      ref={menuRef}
      className={classNames('dropdown-menu', className, {
        'no-vertical-scroll': noVerticalScroll,
        'searchable': isSearchable,
      })}
      style={{
        position: 'absolute',
        top: !isMobile ? position.top : 'unset',
        left: !isMobile ? position.left : 'unset',
        width: optionsAutoWidth ? (!isMobile ? 'auto' : '100%') : (!isMobile ? position.width : '100%'),
        minWidth: !isMobile && optionsAutoWidth ? position.width : 'unset',
        zIndex: 9999,
      }}
      onClick={
        isMobile && !isMulti && onClose
          ? () => onClose()
          : undefined
      }
    >
      {isMobile ? (
        <Flex justify='center' align='center' wfull className='dropdown-mobile-wrapper'>
          <Flex wfull className='dropdown-header' justify='center' align='center' px={2} py={3}>
            <Text variant='h3' align='center'>{title}</Text>
          </Flex>
          {children}
          {isMulti && (
            <Flex wfull className='dropdown-footer' inline gap={2} p={2}>
              <Button
                variant='secondary'
                styles={{ width: '100%' }}
                onClick={closeWithoutSaving}
              >
                Cancel
              </Button>
              <Button
                variant='primary'
                styles={{ width: '100%' }}
                onClick={onClose}
              >
                Select
              </Button>
            </Flex>
          )}
        </Flex>
      ) : children}
    </div>,
    document.body
  )
}

export default DropdownMenu
